import { type UseModalState } from "@clipboard-health/ui-components";
import { Box, DialogActions, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs/FullScreen/Dialog";
import { useCallback, useState } from "react";
import Cropper, { type Area } from "react-easy-crop";

import { Button } from "../../components/Button";
import { LoadingButton } from "../../components/LoadingButton";
import { getSafeAreaPaddings } from "../../utils/getSafeAreaPaddings";
import { getCroppedImageFromArea } from "./utils/getCroppedImageFromArea";

interface ImageCropperModalProps {
  modalState: UseModalState;
  imageDataUrl: string;
  onCropComplete: (croppedBlob: Blob) => Promise<void>;
  onCancel: () => void;
  isUploading?: boolean;
}

export function ImageCropperModal(props: ImageCropperModalProps) {
  const { modalState, imageDataUrl, isUploading = false, onCropComplete, onCancel } = props;

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | undefined>(undefined);

  const onCropCompleteCallback = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!croppedAreaPixels) {
      return;
    }

    const croppedBlob = await getCroppedImageFromArea(imageDataUrl, croppedAreaPixels);
    await onCropComplete(croppedBlob);
  }, [croppedAreaPixels, imageDataUrl, onCropComplete]);

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState}>
      <Box
        sx={{
          flex: 1,
          position: "relative",
          backgroundColor: "common.black",
        }}
      >
        <Cropper
          showGrid
          image={imageDataUrl}
          crop={crop}
          zoom={zoom}
          aspect={1}
          objectFit="contain"
          minZoom={1}
          maxZoom={3}
          style={{
            containerStyle: {
              width: "100%",
              height: "100%",
              backgroundColor: "black",
            },
          }}
          onCropChange={setCrop}
          onCropComplete={onCropCompleteCallback}
          onZoomChange={setZoom}
        />
      </Box>
      <DialogActions
        sx={(theme) => ({
          backgroundColor: "common.black",
          ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(5) }),
        })}
      >
        <Stack spacing={4} width="100%">
          <LoadingButton
            size="medium"
            variant="contained"
            isLoading={isUploading}
            disabled={!croppedAreaPixels || isUploading}
            onClick={handleUpload}
          >
            Upload Photo
          </LoadingButton>
          <Button invert size="medium" variant="outlined" disabled={isUploading} onClick={onCancel}>
            Cancel
          </Button>
        </Stack>
      </DialogActions>
    </FullScreenDialog>
  );
}
